<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { api, type BuildMilestone, type CurrentlyBuildingDocument, type CurrentBuildEntry } from "./api";

const props = defineProps<{ siteId: string }>();
const document = ref<CurrentlyBuildingDocument | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const notice = ref("");
const selectedIndex = ref(0);
const entry = computed(() => document.value?.entries[selectedIndex.value] ?? null);
const completedCount = computed(() => entry.value?.milestones.filter(item => item.isComplete).length ?? 0);

function projectTitle(item: CurrentBuildEntry) {
  return document.value?.availableProjects.find(project => project.id === item.projectId)?.title || item.projectTitle || "Select project";
}

function newMilestone(): BuildMilestone {
  return { title: "New milestone", description: "", isComplete: false };
}

function addEntry() {
  if (!document.value) return;
  const used = new Set(document.value.entries.map(item => item.projectId));
  const project = document.value.availableProjects.find(item => !used.has(item.id));
  if (!project) { error.value = "Every published project is already selected."; return; }
  document.value.entries.push({
    projectId: project.id, projectTitle: project.title, projectSlug: project.slug,
    projectUrl: `/projects/${project.slug}`, externalUrl: "", projectType: "",
    accent: "#2dd4bf", kicker: "Currently building",
    headline: `${project.title} is currently in active development.`, summary: "",
    focusLabel: project.title, milestones: [],
  });
  selectedIndex.value = document.value.entries.length - 1;
}

function removeEntry() {
  if (!document.value) return;
  document.value.entries.splice(selectedIndex.value, 1);
  selectedIndex.value = Math.max(0, Math.min(selectedIndex.value, document.value.entries.length - 1));
}

function moveEntry(direction: -1 | 1) {
  if (!document.value) return;
  const target = selectedIndex.value + direction;
  if (target < 0 || target >= document.value.entries.length) return;
  const [item] = document.value.entries.splice(selectedIndex.value, 1);
  if (item) document.value.entries.splice(target, 0, item);
  selectedIndex.value = target;
}

function moveMilestone(index: number, direction: -1 | 1) {
  if (!entry.value) return;
  const target = index + direction;
  if (target < 0 || target >= entry.value.milestones.length) return;
  const [item] = entry.value.milestones.splice(index, 1);
  if (item) entry.value.milestones.splice(target, 0, item);
}

async function save() {
  if (!document.value || !canEdit.value) return;
  saving.value = true; error.value = ""; notice.value = "";
  try {
    document.value = (await api.updateCurrentlyBuilding(props.siteId, document.value)).currentlyBuilding;
    selectedIndex.value = Math.max(0, Math.min(selectedIndex.value, document.value.entries.length - 1));
    notice.value = "Currently Building saved and available to the website.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save Currently Building";
  } finally { saving.value = false; }
}

onMounted(async () => {
  try {
    const response = await api.currentlyBuilding(props.siteId);
    document.value = response.currentlyBuilding; canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load Currently Building";
  } finally { loading.value = false; }
});
</script>

<template>
  <section class="build-card">
    <div class="module-heading">
      <div><p class="eyebrow">Portfolio activity</p><h2>Currently building</h2><p>Select a published project and explain the work currently in progress.</p></div>
      <button v-if="document" type="button" :disabled="saving || !canEdit" @click="save">{{ saving ? "Saving..." : "Save activity" }}</button>
    </div>
    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <p v-if="loading" class="loading">Loading current work...</p>

    <template v-else-if="document">
      <nav class="entry-tabs" aria-label="Currently building projects">
        <button v-for="(item, index) in document.entries" :key="item.id || index" type="button" :class="{ active: selectedIndex === index }" @click="selectedIndex = index">
          <span>{{ projectTitle(item) }}</span><small>{{ item.milestones.filter(milestone => milestone.isComplete).length }}/{{ item.milestones.length }} complete</small>
        </button>
        <button class="add-tab" type="button" :disabled="!canEdit" @click="addEntry">+ Add active project</button>
      </nav>

      <article v-if="entry" class="entry-editor">
        <div class="entry-toolbar">
          <div><p class="eyebrow">Active project {{ selectedIndex + 1 }}</p><h3>{{ projectTitle(entry) }}</h3></div>
          <div><button type="button" class="mini" :disabled="!canEdit || selectedIndex === 0" @click="moveEntry(-1)">Move left</button><button type="button" class="mini" :disabled="!canEdit || selectedIndex === document.entries.length - 1" @click="moveEntry(1)">Move right</button><button type="button" class="remove" :disabled="!canEdit" @click="removeEntry">Remove</button></div>
        </div>

        <div class="fields">
          <label>Project<select v-model="entry.projectId" :disabled="!canEdit"><option v-for="project in document.availableProjects" :key="project.id" :value="project.id">{{ project.title }}</option></select></label>
          <label>Kicker<input v-model="entry.kicker" :disabled="!canEdit" maxlength="120"></label>
          <label class="wide">Headline<textarea v-model="entry.headline" :disabled="!canEdit" rows="2" maxlength="300" /></label>
          <label class="wide">What are you working on?<textarea v-model="entry.summary" :disabled="!canEdit" rows="5" maxlength="2000" /></label>
          <label class="wide">Focus label<input v-model="entry.focusLabel" :disabled="!canEdit" maxlength="160" placeholder="PBR texture pipeline"></label>
        </div>

        <div class="milestone-heading"><div><h4>Milestones</h4><p>{{ completedCount }}/{{ entry.milestones.length }} complete. The first incomplete item becomes “Next up”.</p></div><button type="button" :disabled="!canEdit" @click="entry.milestones.push(newMilestone())">Add milestone</button></div>
        <div class="milestones">
          <article v-for="(milestone, index) in entry.milestones" :key="milestone.id || index" class="milestone" :class="{ complete: milestone.isComplete }">
            <div class="milestone-toolbar"><strong>{{ String(index + 1).padStart(2, "0") }}</strong><div><button class="mini" type="button" :disabled="!canEdit || index === 0" @click="moveMilestone(index, -1)">↑</button><button class="mini" type="button" :disabled="!canEdit || index === entry.milestones.length - 1" @click="moveMilestone(index, 1)">↓</button><button class="remove" type="button" :disabled="!canEdit" @click="entry.milestones.splice(index, 1)">Remove</button></div></div>
            <label>Title<input v-model="milestone.title" :disabled="!canEdit" maxlength="200"></label>
            <label>Description<textarea v-model="milestone.description" :disabled="!canEdit" rows="3" maxlength="1000" /></label>
            <label class="complete-check"><input v-model="milestone.isComplete" :disabled="!canEdit" type="checkbox"> Complete</label>
          </article>
        </div>
      </article>
      <div v-else class="empty"><p>No active work is displayed.</p><button type="button" :disabled="!canEdit || !document.availableProjects.length" @click="addEntry">Select a project</button></div>
      <div class="module-save"><span>{{ document.entries.length }} active projects</span><button type="button" :disabled="saving || !canEdit" @click="save">{{ saving ? "Saving..." : "Save activity" }}</button></div>
    </template>
  </section>
</template>

<style scoped>
.build-card{display:grid;gap:1.25rem;padding:1.5rem;border:1px solid #d9ddd8;border-radius:1rem;background:#fff}.module-heading,.entry-toolbar,.module-save,.milestone-heading,.milestone-toolbar{display:flex;align-items:center;justify-content:space-between;gap:1rem}.module-heading h2,.entry-toolbar h3,.milestone-heading h4{margin:0}.module-heading p:last-child,.milestone-heading p{margin:.35rem 0 0;color:#6d7972}.eyebrow{margin:0 0 .25rem;color:#6b7b72;font-size:.68rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.entry-tabs{display:flex;overflow-x:auto;gap:.35rem;padding:.35rem;border:1px solid #dce2dc;border-radius:.75rem;background:#f5f7f5}.entry-tabs button{min-width:max-content;color:#456055;background:transparent}.entry-tabs button.active{color:#fff;background:#285c47}.entry-tabs small{display:block;margin-top:.15rem;font-size:.62rem}.entry-tabs .add-tab{border:1px dashed #8ea096}.entry-editor{display:grid;gap:1.25rem;padding:1.2rem;border:1px solid #dce2dc;border-radius:.8rem;background:#fafbf9}.entry-toolbar>div:last-child,.milestone-toolbar>div{display:flex;gap:.4rem}.mini{padding:.5rem .65rem;color:#365045;background:#e8eeea}.remove{padding:.5rem .65rem;color:#91382f;background:#fae9e7}.fields{display:grid;grid-template-columns:1fr 1fr;gap:.85rem}.fields .wide{grid-column:1/-1}label{display:grid;gap:.4rem}select,textarea{width:100%;padding:.75rem;border:1px solid #cbd5ce;border-radius:.65rem;background:#fff;font:inherit}textarea{resize:vertical}.milestone-heading{padding-top:1rem;border-top:1px solid #dce2dc}.milestone-heading button{color:#315342;background:#e8eeea}.milestones{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.8rem}.milestone{display:grid;gap:.75rem;padding:1rem;border:1px solid #dce2dc;border-radius:.7rem;background:#fff}.milestone.complete{background:#f0f6f2}.milestone-toolbar strong{color:#285c47}.complete-check{display:flex;align-items:center;gap:.45rem}.complete-check input{width:auto}.module-save{padding-top:1rem;border-top:1px solid #e1e5e1;color:#6d7972;font-size:.8rem}.message{margin:0;padding:.85rem 1rem;border-radius:.65rem}.message.error{color:#86251f;background:#fee9e7}.message.success{color:#295b40;background:#e2f1e7}.loading,.empty{padding:2rem;text-align:center}@media(max-width:700px){.module-heading,.entry-toolbar,.module-save,.milestone-heading{align-items:stretch;flex-direction:column}.fields,.milestones{grid-template-columns:1fr}.fields .wide{grid-column:auto}}
</style>
